import { getIsMigrationTradingBlocked } from '@/hooks/use-migration';
import { ordersApi } from '@/lib/api-clients/rest-client';
import { useAccountStore } from '@/store/use-account-store';
import { useOrdersStore } from '@/store/use-orders-store';
import { ErrorCode, getErrorMsg, OrderStatus } from '@/types/enums';
import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { encodeAbiParameters, keccak256 } from 'viem';
import { signMessage } from 'viem/accounts';

export type CancelOrderResponse = {
  status: OrderStatus.CANCELLED;
  cancelledIds: string[];
};

type CancelOrdersParams = {
  id?: string[];
  showToast: boolean;
};

export default function useCancelOrders(
  options?: UseMutationOptions<CancelOrderResponse, Error, CancelOrdersParams>,
) {
  const { t } = useTranslation();
  return useMutation({
    ...(options ?? {}),
    mutationFn: async ({
      id,
      showToast,
      // }: CancelOrdersParams): Promise<CancelOrderResponse[]> => {
    }: CancelOrdersParams): Promise<CancelOrderResponse> => {
      if (getIsMigrationTradingBlocked()) {
        throw ErrorCode.ACCOUNT_MIGRATION_LOCKED;
      }

      const { getAuthParams, nextNonce, address, getAccurateTime } =
        useAccountStore.getState();
      const authParams = getAuthParams();

      if (!authParams?.signingKey) {
        console.error('No signing key found');
        throw new Error(
          t ? t('cancelOrder.noSigningKeyFound') : 'No signing key found',
        );
      }

      const time = getAccurateTime();

      let ids: Set<string> = new Set();

      if (id) {
        ids = ids.union(new Set(id));
      } else {
        ids = ids.union(
          useOrdersStore.getState().getOrders(address).newOrderIds,
        );
      }

      if (ids.size === 0) {
        throw new Error('No orders to cancel.');
      }

      const orders = await Promise.all(
        Array.from(ids, async (id) => {
          const nonce = nextNonce();

          const encodedOrder = encodeAbiParameters(
            [
              { name: 'time', type: 'uint256' },
              { name: 'nonce', type: 'uint256' },
              { name: 'id', type: 'string' },
            ],
            [BigInt(time), BigInt(nonce), id],
          );

          const signature = await signMessage({
            message: { raw: keccak256(encodedOrder) },
            privateKey: authParams.signingKey,
          });

          return {
            order: { id, nonce, time },
            signature,
            recvWindow: 0,
          };
        }),
      );

      const restPromise = ordersApi.post<CancelOrderResponse>(
        '/orders/cancel/scale',
        {
          orders,
        },
      );

      const confirmPromise = restPromise.then((res) => {
        const promise = new Promise<Set<string>>((resolve, reject) => {
          let subscriberTimeoutId: NodeJS.Timeout;
          let unsubscribe: () => void;

          const cleanup = (cancelledIds: Set<string>) => {
            clearTimeout(subscriberTimeoutId);
            unsubscribe();
            if (cancelledIds.size > 0) {
              resolve(cancelledIds);
            } else {
              reject(ErrorCode.INTERNAL_CREATE_ORDER_TIMEOUT);
            }
          };

          unsubscribe = useOrdersStore.subscribe((state) => {
            const { cancelledOrderIds, filledOrderIds } =
              state.getOrders(address);
            const hasFinished = res.cancelledIds.every(
              (id) => cancelledOrderIds.has(id) || filledOrderIds.has(id),
            );
            if (hasFinished) {
              cleanup(
                cancelledOrderIds.intersection(new Set(res.cancelledIds)),
              );
            }
          });

          subscriberTimeoutId = setTimeout(() => {
            const { cancelledOrderIds } = useOrdersStore
              .getState()
              .getOrders(address);
            cleanup(cancelledOrderIds.intersection(new Set(res.cancelledIds)));
          }, 30_000); // 30 seconds timeout
        });

        useOrdersStore
          .getState()
          .pollOrders(
            res.cancelledIds.map((id) => [id, OrderStatus.CANCELLED, time]),
          );

        return promise;
      });

      if (showToast) {
        const loadingId = toast.loading(
          orders.length > 1
            ? t('cancelOrder.loadingMultiple', { count: orders.length })
            : t('cancelOrder.loadingSingle'),
        );
        confirmPromise.then((cancelledIds) => {
          toast.dismiss(loadingId);
          toast.success(
            cancelledIds.size > 1
              ? t('cancelOrder.successMultiple', { count: cancelledIds.size })
              : t('cancelOrder.successSingle'),
          );
          if (cancelledIds.size < ids.size) {
            const { orders } = useOrdersStore.getState().getOrders(address);
            const alreadyFilled: Set<string> = new Set();
            const timeoutFailed: Set<string> = new Set();
            Array.from(ids.difference(cancelledIds), (id) => {
              switch (orders[id].status) {
                case OrderStatus.FILLED:
                  alreadyFilled.add(id);
                  break;
                default:
                  timeoutFailed.add(id);
                  break;
              }
            });
            if (alreadyFilled.size > 0) {
              toast.error(
                alreadyFilled.size > 1
                  ? t('cancelOrder.filledSingle')
                  : t('cancelOrder.filledMultiple', {
                      count: alreadyFilled.size,
                    }),
              );
            }
            if (timeoutFailed.size > 0) {
              const message =
                timeoutFailed.size > 1
                  ? t('cancelOrder.failedMultiple', {
                      count: timeoutFailed.size,
                    })
                  : t('cancelOrder.failedSingle');
              toast.error(
                message +
                  getErrorMsg(ErrorCode.INTERNAL_CREATE_ORDER_TIMEOUT, t),
              );
            }
          }
        });

        confirmPromise.catch((err: ErrorCode) => {
          const message =
            ids.size > 1
              ? t('cancelOrder.failedMultiple', {
                  count: ids.size,
                })
              : t('cancelOrder.failedSingle');
          toast.error(message + getErrorMsg(err, t));
          toast.dismiss(loadingId);
        });
      }

      return restPromise;
    },
  });
}
